import {
  Button,
  SearchBox,
  OutlinedSelect,
  SelectOptionType,
} from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { StudentsRemovalRequestsPendingFiltersResponse } from '../types';
import { useStudentsRemovalRequestsPendingtList } from './StudentsRemovalRequestsPendingListLoader';

interface StudentsRemovalRequestsPendingFiltersForm {
  studentNameOrId: string;
  studyPlanVersion: SelectOptionType | null;
  requestState: SelectOptionType | null;
}

export function StudentsRemovalRequestsPendingFilters({
  data,
}: {
  data: StudentsRemovalRequestsPendingFiltersResponse;
}) {
  const { updateQuery } = useStudentsRemovalRequestsPendingtList();
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_MANAGEMENT_STUDENT_REMOVAL_REQUEST_MANAGEMENT_PENDING,
  );
  const texts = useMemo(() => {
    return t<
      string,
      {
        student: string;
        studentPlaceholder: string;
        studyPlanVersionName: string;
        studyPlanVersionNamePlaceholder: string;
        requestState: string;
        requestStatePlaceholder: string;
        search: string;
        clean: string;
      }
    >('requests-pending.filters', { returnObjects: true });
  }, [t]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
  } = useForm<StudentsRemovalRequestsPendingFiltersForm>({
    defaultValues: {
      studentNameOrId: '',
      studyPlanVersion: null,
      requestState: null,
    },
  });

  const { validateMinLength } = useValidations();

  const localSubmit = useCallback(
    (values: StudentsRemovalRequestsPendingFiltersForm) => {
      console.log({ values });
      updateQuery({
        page: 1,
        studentNameOrId: values.studentNameOrId,
        studyPlanVersionId:
          typeof values.studyPlanVersion?.value === 'number'
            ? values.studyPlanVersion?.value
            : undefined,
      });
    },
    [updateQuery],
  );

  const studyPlanVersionsOptions = useMemo(() => {
    return data.studyPlanVersions.map((s) => ({
      value: s.id,
      label: s.name,
    }));
  }, [data.studyPlanVersions]);

  return (
    <form onSubmit={handleSubmit(localSubmit)}>
      <Row>
        <Col xs={12} md={4}>
          <SearchBox
            name="studentNameOrId"
            label={texts.student}
            placeholder={texts.studentPlaceholder}
            control={control}
            clear={() => setValue('studentNameOrId', '')}
            minLength={3}
            rules={{
              validate: {
                minLength: validateMinLength(3),
              },
            }}
          />
        </Col>
        <Col xs={12} md={4}>
          <OutlinedSelect
            control={control}
            name="studyPlanVersion"
            label={texts.studyPlanVersionName}
            placeholder={texts.studyPlanVersionNamePlaceholder}
            options={studyPlanVersionsOptions}
          />
        </Col>
        <Col xs={12} md={4} className="ml-auto">
          <div className="d-flex justify-content-end mt-4">
            <Button
              type="submit"
              color="primary"
              className="mb-3"
              size="md"
              text={texts.search}
            />
            <Button
              type="submit"
              color="primary"
              outlined
              className="mb-3 ml-2"
              onClick={() => {
                reset();
              }}
              size="md"
              text={texts.clean}
            />
          </div>
        </Col>
      </Row>
    </form>
  );
}
